import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { organizationsHydratedAtom, organizationsListParamsAtom } from '@/store';

export interface SearchFields {
  name: string;
  user_email: string;
}

export function useOrganizationsFilters() {
  const [params, setParams] = useAtom(organizationsListParamsAtom);
  const [, setHydrated] = useAtom(organizationsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    name: parseAsString.withDefault(''),
    user_email: parseAsString.withDefault(''),
    id: parseAsInteger,
    date: parseAsString.withDefault('')
  });

  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    name: queryParams.name,
    user_email: queryParams.user_email
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);

  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  React.useEffect(() => {
    // Mark hydrated immediately to enable the query
    setHydrated(true);

    const filters: Record<string, string | number | undefined> = {};

    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.name) filters.name = queryParams.name;
    if (queryParams.user_email) filters.user_email = queryParams.user_email;
    if (queryParams.id) filters.id = queryParams.id;
    if (queryParams.date) filters.date = queryParams.date;

    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters: filters
      };

      if (prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)) {
        return prev;
      }

      return newParams;
    });
  }, [queryParams.page, queryParams.search, queryParams.name, queryParams.user_email, queryParams.id, setParams, setHydrated]);

  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setSearchFields({
      name: queryParams.name,
      user_email: queryParams.user_email
    });
  }, [queryParams]);

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      name: searchFields.name || undefined,
      user_email: searchFields.user_email || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));
    await setQueryParams({
      page: 1,
      name: searchFields.name,
      user_email: searchFields.user_email
    });
    setIsSearchOpen(false);
  }, [params.filters, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setSearchFields({ name: '', user_email: '' });
    setDateFilter({ start: null, end: null });
    setParams((prev) => ({ ...prev, page: 1, filters: {}, refresh: Date.now() }));
    await setQueryParams({ page: 1, search: '', name: '', user_email: '', id: undefined, date: '' });
  }, [setParams, setQueryParams]);

  React.useEffect(() => {
    if (queryParams.date) {
      const dates = queryParams.date.split(',');
      if (dates.length === 2) {
        setDateFilter({
          start: dates[0] ? new Date(dates[0]) : null,
          end: dates[1] ? new Date(dates[1]) : null
        });
      } else if (dates.length === 1 && dates[0]) {
        setDateFilter({
          start: new Date(dates[0]),
          end: null
        });
      } else {
        setDateFilter({ start: null, end: null });
      }
    } else {
      setDateFilter({ start: null, end: null });
    }
  }, [queryParams.date]);

  const hasActiveFilters = React.useMemo(() => {
    return Boolean(
      searchTerm ||
        searchFields.name ||
        searchFields.user_email ||
        queryParams.id ||
        dateFilter.start ||
        dateFilter.end
    );
  }, [searchTerm, searchFields, queryParams.id, dateFilter]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  const handleDateChange = React.useCallback((value: { start: Date | null; end: Date | null }) => {
    setDateFilter(value);

    let dateFilterValue = '';
    if (value.start && value.end) {
      const startDate = value.start.toISOString().split('T')[0];
      const endDate = value.end.toISOString().split('T')[0];
      dateFilterValue = `${startDate},${endDate}`;
    } else if (value.start) {
      dateFilterValue = value.start.toISOString().split('T')[0];
    }

    setQueryParams({ page: 1, date: dateFilterValue });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        date: dateFilterValue || undefined
      }
    }));
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    searchFields,
    setSearchFields,
    isSearchOpen,
    setIsSearchOpen,
    hasActiveFilters,
    dateFilter,

    // Actions
    handleSearch,
    handleClearSearch,
    applyFilters,
    clearFilters,
    handleDateChange,
    handlePageChange
  };
}
